import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { AdminPageHeader, AdminStatusBadge, AdminTableCard } from './AdminUi';
import './admin.css';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'PAYABLE', label: 'Payable' },
  { value: 'NO_SHOW_PATIENT_PAYABLE', label: 'No-show payable' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PENDING_REVIEW', label: 'Pending review' },
  { value: 'HOLD', label: 'Hold' },
];

export default function AdminEarnings() {
  const [status, setStatus] = useState('');
  const [data, setData] = useState({ total: 0, results: [] });
  const [payouts, setPayouts] = useState({ total: 0, results: [] });
  const [payingId, setPayingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('admin/earnings/', { params: { status: status || undefined, limit: 100 } }),
      api.get('admin/payouts/', { params: { limit: 50 } }),
    ])
      .then(([e, p]) => {
        setData(e.data);
        setPayouts(p.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Failed'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status]);

  const payOne = async (id) => {
    setPayingId(id);
    try {
      const r = await api.post('admin/earnings/pay/', { earning_ids: [id] });
      toast.success(r.data.msg || 'Paid');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Payout failed');
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div>
      <AdminPageHeader title="Earnings & Payouts" subtitle={`${data.total} earning records`} />

      <div className="admin-filter-chips mb-3">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            className={`admin-chip ${status === f.value ? 'active' : ''}`}
            onClick={() => setStatus(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <div className="text-center py-5"><div className="spinner-border text-primary" /></div> : (
        <>
          <AdminTableCard>
            <table className="admin-table admin-table-earnings">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Doctor</th>
                  <th>Appt</th>
                  <th>Doctor $</th>
                  <th>Fee</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.results.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">No earnings found</td>
                  </tr>
                ) : (
                  data.results.map((e) => {
                    const payable = ['PAYABLE', 'NO_SHOW_PATIENT_PAYABLE'].includes(e.status);
                    return (
                      <tr key={e.id}>
                        <td>{e.id}</td>
                        <td>
                          <div className="fw-semibold">{e.doctor_name}</div>
                          <div className="small text-muted">{e.doctor_email}</div>
                        </td>
                        <td>{e.appointment_id}</td>
                        <td className="fw-bold">${Number(e.doctor_amount).toFixed(2)}</td>
                        <td className="small">${Number(e.consultation_fee).toFixed(2)}</td>
                        <td className="admin-col-shrink">
                          <AdminStatusBadge status={e.status} />
                        </td>
                        <td className="admin-col-shrink">
                          {payable ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-success rounded-pill px-3"
                              disabled={payingId === e.id}
                              onClick={() => payOne(e.id)}
                            >
                              {payingId === e.id ? 'Paying…' : 'Pay'}
                            </button>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </AdminTableCard>

          <h5 className="fw-bold mt-4 mb-2">Payout history</h5>
          <AdminTableCard>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th><th>Doctor</th><th>Amount</th><th>Count</th><th>Status</th><th>Paid at</th>
                </tr>
              </thead>
              <tbody>
                {payouts.results.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.doctor_name}</td>
                    <td className="fw-bold">${Number(p.amount).toFixed(2)}</td>
                    <td>{p.earnings_count}</td>
                    <td><AdminStatusBadge status={p.status} /></td>
                    <td className="small">{p.paid_at ? new Date(p.paid_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableCard>
        </>
      )}
    </div>
  );
}
